import clsx from 'clsx';
import { useLayoutEffect, useRef, type CSSProperties, type ReactNode } from 'react';

interface ScrollableProps {
    children: ReactNode;
    className?: string;
    variant?: 'horizontal' | 'vertical';
    itemsInView?: number;
    itemsToScroll?: number;
    itemHeight?: number | string;
}

const Scrollable = ({
    children,
    className,
    variant = 'vertical',
    itemsInView,
    itemsToScroll,
    itemHeight,
}: ScrollableProps) => {
    const containerRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
        const container = containerRef.current;
        if (!container || variant !== 'vertical' || itemsInView == null) {
            return;
        }

        const applyMaxHeight = () => {
            if (itemHeight != null) {
                const height = typeof itemHeight === 'number' ? `${itemHeight}px` : itemHeight;
                container.style.setProperty('--scrollable-item-height', height);
                container.style.removeProperty('max-height');
                return;
            }

            const rows = Array.from(container.children) as HTMLElement[];
            if (rows.length === 0) {
                return;
            }

            const visibleRows = rows.slice(0, Math.min(itemsInView, rows.length));
            if (visibleRows.length === 0) {
                return;
            }

            const first = visibleRows[0];
            const last = visibleRows[visibleRows.length - 1];
            const visibleHeight = last.offsetTop + last.offsetHeight - first.offsetTop;

            container.style.removeProperty('--scrollable-item-height');
            container.style.maxHeight = `${visibleHeight}px`;
        };

        applyMaxHeight();

        const resizeObserver = new ResizeObserver(applyMaxHeight);
        resizeObserver.observe(container);
        Array.from(container.children).forEach((child) => resizeObserver.observe(child));

        return () => resizeObserver.disconnect();
    }, [children, itemHeight, itemsInView, variant]);

    const style: CSSProperties | undefined =
        itemsInView != null && itemHeight != null
            ? {
                  ['--scrollable-items-in-view' as string]: itemsInView,
                  ['--scrollable-item-height' as string]:
                      typeof itemHeight === 'number' ? `${itemHeight}px` : itemHeight,
                  ...(itemsToScroll != null ? { ['--scrollable-items-to-scroll' as string]: itemsToScroll } : {}),
              }
            : undefined;

    return (
        <div ref={containerRef} className={clsx('scrollable', variant, className)} style={style} tabIndex={0}>
            {children}
        </div>
    );
};

export default Scrollable;
